import {
  ORG_PARAMLESS_PATHS,
  ORG_PATH_PREFIX,
  RESOURCE_ID_SEGMENT_RE,
  orgBasePath,
} from '@/lib/routes';

/**
 * Build the destination URL when switching orgs. The URL is the source of
 * truth for the active org, so we swap the slug segment when the current
 * path is org-scoped (`/orgs/old/runs` -> `/orgs/new/runs`) or jump to the
 * new org's home (`/orgs/new`) when the current path is not org-scoped
 * (admin, profile, settings, etc.).
 *
 * A resource id must never cross the org boundary. `/orgs/old/agents/agt_x`
 * names a row that exists in the old org only, so carrying it over lands on
 * a deep link that 404s in the new org while looking like an empty
 * workspace. A path holding a segment shaped like a resource id
 * ({@link RESOURCE_ID_SEGMENT_RE}: our own `agt_…`/`cnv_…` and provider ids
 * like `bpt_…`/`snp_…` alike) is therefore cut back to its nearest ancestor
 * that is a real paramless page ({@link ORG_PARAMLESS_PATHS}) — `/chat/cnv_x`
 * -> `/chat`, which picks or creates a chat in the new org — and to the new
 * org's home, which every org has, when it has no such ancestor.
 *
 * Two known gaps, both of which land the switch somewhere safe rather than
 * somewhere wrong:
 *
 * - Only `<prefix>_<base62>` ids are recognised, so an org-scoped id in some
 *   other shape (a bare numeric or slug-style provider key, say) still
 *   travels. The generic safety net for that is the destination page: a
 *   resource lookup that fails must render a not-found state, never an
 *   empty-looking placeholder.
 * - {@link ORG_PARAMLESS_PATHS} covers host routes only, so a plugin's
 *   paramless list page is never picked as the truncation target and a
 *   switch from `/projects/prj_x` lands on the new org's home rather than
 *   its project list.
 */
export function buildSwitchTarget(
  pathname: string,
  knownSlugs: ReadonlySet<string>,
  newSlug: string,
): string {
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] !== ORG_PATH_PREFIX || !segments[1] || !knownSlugs.has(segments[1])) {
    return orgBasePath(newSlug);
  }

  const orgRelative = segments.slice(2);
  const idIndex = orgRelative.findIndex((segment) => RESOURCE_ID_SEGMENT_RE.test(segment));
  if (idIndex === -1) return `${orgBasePath(newSlug)}${asPath(orgRelative)}`;

  for (let end = idIndex; end > 0; end--) {
    const candidate = asPath(orgRelative.slice(0, end));
    if (ORG_PARAMLESS_PATHS.has(candidate)) return `${orgBasePath(newSlug)}${candidate}`;
  }
  return orgBasePath(newSlug);
}

/** Org-relative segments as a path (`[]` is the org home, which is `''`). */
function asPath(segments: string[]): string {
  return segments.length > 0 ? `/${segments.join('/')}` : '';
}
